import { Component, JSX } from "react";

import { ProjectService } from "@app/projects";
import { CrossProjectGapModel } from "@app/projects/network-assessment/adequacy";
import { FilteredResource, Resource, ResourceStatus } from "@app/resources";
import { formatDateTime } from "@app/shared";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import CrossProjectGapsProjectView from "./CrossProjectGapsProjectView";
import { FlyoutContextOutgoingProps, withFlyoutContext } from "@app/shared/Flyouts";

type CrossProjectGapsProps = FlyoutContextOutgoingProps & {
  filteredProjectResources: FilteredResource[];
  projectResources: Resource[];
  resourceStatus: ResourceStatus;
}

interface CrossProjectGapsState {
  asOfDate?: Date;
  loadStatus: LoadStatus;
  offset: number;
  selectedGap: CrossProjectGapModel;
  sortedRecords: CrossProjectGapsTableData[];
  totalCount: number;
}

interface CrossProjectGapsTableData {
  [wafflesKeyColumnName]: string;
  analysisRegion: string;
  gaps: JSX.Element;
  specialtyGroup: string;
}

const columnConfig: ColumnConfig = {
  analysisRegion: {
    displayText: "Analysis Region",
    testId: "analysis-region",
  },
  gaps: {
    displayText: "Gaps",
    testId: "gaps",
  },
  specialtyGroup: {
    displayText: "Specialty Group",
    testId: "specialty-group",
  },
};

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

class WrappedCrossProjectGaps extends Component<CrossProjectGapsProps, CrossProjectGapsState> {
  private readonly batchCount: number = 40;
  private readonly projectService = new ProjectService();

  constructor(props: CrossProjectGapsProps) {
    super(props);

    this.state = {
      asOfDate: null,
      loadStatus: "shouldLoad",
      offset: 0,
      selectedGap: null,
      sortedRecords: [],
      totalCount: 0,
    };
  }

  public async componentDidMount() {
    await this.getCrossProjectGapsResult();
  }

  public componentDidUpdate(prevProps: Readonly<CrossProjectGapsProps>, prevState: Readonly<CrossProjectGapsState>): void {
    if (!prevState.selectedGap && !!this.state.selectedGap) {
      this.props.setTitle(`Gaps by Project for ${this.state.selectedGap.specialtyGroupDisplayName} in ${this.state.selectedGap.regionDisplayName}`);
    } else if (!!prevState.selectedGap && !this.state.selectedGap) {
      this.props.setTitle("Gaps by County and Specialty");
    }
  }

  public render() {
    if (this.state && this.state.selectedGap) {
      return (
        <CrossProjectGapsProjectView
          asOfDate={this.state.asOfDate}
          filterPath={this.state.selectedGap.filterPath}
          gapLinkId={this.state.selectedGap.gapLinkId}
          onBreadCrumbClick={this.handleProjectViewBreadCrumbClick}
          projectResources={this.props.projectResources}
          regionDisplayName={this.state.selectedGap.regionDisplayName}
          specialtyGroupDisplay={this.state.selectedGap.specialtyGroupDisplayName}
        />
      );
    } else {
      return this.renderCrossProjectGapsView();
    }
  }

  private renderCrossProjectGapsView = () => {
    const shouldShowBatchCount = !!this.state && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading");

    return (
      <>
        {this.state.asOfDate && <small className="pull-right">As of {formatDateTime(this.state.asOfDate)}</small>}
        {shouldShowBatchCount && <small>Displaying {this.state.sortedRecords.length} of {this.state.totalCount} rows</small>}
        {this.renderCrossProjectGapTable()}
      </>
    );
  }

  private renderCrossProjectGapTable = () => {
    return (
      <Waffles<CrossProjectGapsTableData>
        columns={columnConfig}
        customSort={this.handleCustomSort}
        data={this.state.sortedRecords}
        fullWidth={true}
        infiniteScroll={false}
        loading={!this.state || this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad"}
        loadMore={this.handleLoadMore}
        passThroughSorting={true}
        testId="cross-project-gaps-table"
      />
    );
  }

  private getCrossProjectGapsResult = async (sortDirection?: SortDirection, sortedColumn?: keyof CrossProjectGapModel, sorting?: boolean) => {
    try {
      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad"
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : this.state.loadStatus === "loaded" ? "loaded" : null;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        const crossProjectGapsResult = await this.projectService.crossProjectGapsResult(
          this.props.filteredProjectResources,
          sorting ? 0 : this.state.offset,
          this.batchCount,
          sorting ? sortDirection : undefined,
          sorting ? sortedColumn : undefined,
        );

        let loadStatus: LoadStatus = "error";
        let updatedRecords: CrossProjectGapsTableData[] = [];
        const records: CrossProjectGapsTableData[] = crossProjectGapsResult.crossProjectGapModels.map((cpgm) => {
          /* eslint-disable sort-keys */
          return {
            [wafflesKeyColumnName]: cpgm.gapLinkId.toString(),
            analysisRegion: cpgm.regionDisplayName,
            specialtyGroup: cpgm.specialtyGroupDisplayName,
            gaps: cpgm.gapCount > 0 ?
              (
                <QESButton qesType="secondary" qesTestId={`${cpgm.gapLinkId}-gap-link`} onClick={() => this.setState({ selectedGap: cpgm })}>
                  {cpgm.gapCount}
                </QESButton>
              )
              : <>{cpgm.gapCount}</>,
          };
          /* eslint-enable sort-keys */
        });

        if (crossProjectGapsResult !== null && crossProjectGapsResult.crossProjectGapModels !== null) {
          if (crossProjectGapsResult.crossProjectGapModels.length > 0) {
            loadStatus = "loaded";
            if (!sorting) {
              updatedRecords = [...this.state.sortedRecords, ...records];
            } else {
              updatedRecords = records;
            }
          } else {
            loadStatus = "empty";
          }
        }
        this.setState((prevState) => ({
          ...prevState,
          ...crossProjectGapsResult,
          asOfDate: crossProjectGapsResult && crossProjectGapsResult.asOfDate,
          loadStatus,
          offset: sorting ? this.batchCount : crossProjectGapsResult ? prevState.offset + crossProjectGapsResult.batchCount : prevState.offset,
          sortedRecords: updatedRecords,
        }));
      }
    } catch (ex) {
      this.setState((prevState): CrossProjectGapsState => ({
        ...prevState,
        asOfDate: null,
        loadStatus: "error",
        sortedRecords: [],
      }));
      console.error(ex);
    }
  }

  private handleCustomSort = (records: CrossProjectGapsTableData[], column: keyof CrossProjectGapsTableData, direction: SortDirections) => {
    let sortDirection: SortDirection;
    let key: keyof CrossProjectGapModel;

    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;
    }

    switch (column) {
      case "analysisRegion":
        key = "regionDisplayName";
        break;
      case "specialtyGroup":
        key = "specialtyGroupDisplayName";
        break;
      case "gaps":
        key = "gapCount";
        break;
    }

    this.setState({
      loadStatus: "shouldLoad",
    }, () => this.getCrossProjectGapsResult(sortDirection, key, true));
    return records;
  }

  private handleLoadMore = (justChecking: boolean) => {
    // need to define this before the async getCrossProjectGapsResult just to make sure state hasn't been updated
    const shouldShowButton = this.state.offset < this.state.totalCount;
    if (!justChecking) {
      this.getCrossProjectGapsResult();
    }
    return shouldShowButton;
  }

  private handleProjectViewBreadCrumbClick = () => {
    this.setState({ selectedGap: null });
  }
}

const CrossProjectGaps = withFlyoutContext(WrappedCrossProjectGaps);
export default CrossProjectGaps;
